import React, { useState } from "react"
import { connect, useDispatch } from "react-redux"
import { Input, Button } from "reactstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/free-solid-svg-icons"
import { setCurrentPage } from "../../store/reducers/navigation"
import {
  clearSelectedSearchMembers,
  removeSelectedSearchMember,
} from "../../store/actions/members/searchUsers"
import { Trans, useLingui } from "@lingui/react"
import { toast } from "react-toastify"
import uuid from "uuid"
import { createMember } from "../../store/actions/members/createMember"

const notify = (msg) => toast(msg)

const SuccessMessage = () => (
  <div style={{ color: "green" }}>
    <Trans id="user_added" />
  </div>
)
const ErrorMessage = () => (
  <div style={{ color: "red" }}>
    <Trans id="user_not_added" />
  </div>
)
const IncompleteMessage = () => (
  <div style={{ color: "orange" }}>
    <Trans id="incomplete_fields" />
  </div>
)
const AlreadyInGroupMessage = () => (
  <div style={{ color: "red" }}>
    <Trans id="already_in_group" />
  </div>
)
const NoMembersMessage = () => (
  <div style={{ color: "orange" }}>
    <Trans id="no_members_to_assign"></Trans>
  </div>
)

const AssignMemberRoles = ({
  selectedMembers,
  clearSelectedSearchMembers,
  removeSelectedSearchMember,
  setPage,
  group,
  addMember,
}) => {
  const { i18n } = useLingui()
  const [assignedRoles, setAssignedRoles] = useState(
    selectedMembers.map((m) => ({
      ...m,
      role: "",
      paysInterest: "",
      errors: {},
    })),
  )

  const language = i18n.locale

  const handleChange = (id, field, value) => {
    setAssignedRoles((prev) =>
      prev.map((member) =>
        member.id === id
          ? {
            ...member,
            [field]: value,
            errors: {
              ...member.errors,
              [field]: !value,
            },
          }
          : member,
      ),
    )
  }

  const removeUser = (id) => {
    setAssignedRoles((prev) => prev.filter((user) => user.id !== id))
    removeSelectedSearchMember(id)
  }

  const handleSubmit = async () => {
    if (assignedRoles.length === 0) {
      notify(<NoMembersMessage />)
      return
    }

    const group_name = group?.name || <Trans id="vsla_group" />

    const notif = {
      en: {
        message: `You have been added as a member of ${group_name} savings group`,
      },
      fr: {
        message: `You have been added as a member of ${group_name} savings group`,
      },
      sw: {
        message: `Umeandikishwa kuwa mwanachama wa kikundi cha akiba ${group_name}`,
      },
      es: {
        message: `Se le ha agregado como miembro del grupo de ahorro ${group_name}`,
      },
      lb: {
        message: `You have been added as a household of ${group_name} community`,
      },
      pt: {
        message: `Você foi adicionado como membro do grupo de poupança ${group_name}`,
      },
      ar: {
        message: group_name,
      },
    }

    const hasMissingFields = assignedRoles.some((m) => !m.role || !m.paysInterest)

    if (hasMissingFields) {
      setAssignedRoles((prev) =>
        prev.map((m) => ({
          ...m,
          errors: {
            ...m.errors,
            role: !m.role,
            paysInterest: !m.paysInterest,
          },
        })),
      )
      notify(<IncompleteMessage />)
      return
    }

    try {
      for (const member of assignedRoles) {
        const memberPayload = {
          member_id: member.id,
          member_role: member.role,
          gender: member.gender,
          nationalities: member.nationalities,
          full_name: member.full_name,
          phone: member.phone,
          pubkey: uuid.v4(),
          date_of_birth: member.date_of_birth,
          status: "Active",
          loan_service_charge: member.paysInterest === "Yes",
          msg: notif[language]?.message || notif.en.message,
          secretary_id: group?.blockchain_key || "",
        }
        addMember(memberPayload, true)
      }

      notify(<SuccessMessage />)
      clearSelectedSearchMembers()
      setPage("group-memberList")
    } catch (err) {
      notify(<ErrorMessage />)
    }
  }

  return (
    <div style={styles.container}>
      <h4 style={styles.title}>
        <Trans id="assign_roles_to_members" />
      </h4>
      {assignedRoles.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>
            <Trans id="no_members_found" />
          </p>
          <p style={styles.emptySubText}>
            <Trans id="need_to_select_members" />
          </p>
          <Button
            color="primary"
            onClick={() => setPage("group-addExistingMembers")}
          >
            <Trans id="go_back_and_select_members" />
          </Button>
        </div>
      ) : (
        <>
          {assignedRoles.map((user) => (
            <div
              key={user.id}
              style={{
                ...styles.card,
                border: user.errors?.alreadyInGroup ? "1px solid #f44336" : "none",
                backgroundColor: user.errors?.alreadyInGroup ? "#fff5f5" : "#fafafa",
              }}
            >
              <div style={styles.cardHeader}>
                <div style={styles.name}>{user.full_name}</div>
                {user.errors?.alreadyInGroup && (
                  <FontAwesomeIcon
                    icon={faTimes}
                    style={styles.removeIcon}
                    onClick={() => removeUser(user.id)}
                  />
                )}
              </div>

              {user.errors?.alreadyInGroup && (
                <p style={styles.groupError}>
                  <Trans id="user_already_in_group" />
                </p>
              )}

              <Input
                type="select"
                value={user.role}
                onChange={(e) => handleChange(user.id, "role", e.target.value)}
                style={{
                  ...styles.select,
                  borderColor: user.errors?.role ? "#f44336" : "#e36f1e",
                  backgroundColor: user.errors?.role ? "#fff5f5" : "#fff",
                }}
              >
                <option value="">
                  <Trans id="select_role" />
                </option>
                <option value="Chairperson">
                  <Trans id="chairperson" />
                </option>
                {!["en", "lb"].includes(language) && (
                  <>
                    <option value="Secretary">
                      <Trans id="secretary" />
                    </option>
                    <option value="Box Keeper">
                      <Trans id="box_keeper" />
                    </option>
                    <option value="Money Counter">
                      <Trans id="money_counter" />
                    </option>
                    <option value="Organiser">
                      <Trans id="organiser" />
                    </option>
                    <option value="Key Holder">
                      <Trans id="keyHolder" />
                    </option>
                  </>
                )}
                <option value="Member">
                  <Trans id="member" />
                </option>
              </Input>

              <Input
                type="select"
                value={user.paysInterest}
                onChange={(e) =>
                  handleChange(user.id, "paysInterest", e.target.value)
                }
                style={{
                  ...styles.select,
                  marginTop: "8px",
                  borderColor: user.errors?.paysInterest ? "#f44336" : "#e36f1e",
                  backgroundColor: user.errors?.paysInterest ? "#fff5f5" : "#fff",
                }}
              >
                <option value="">
                  <Trans id="pays_interest" />
                </option>
                <option value="Yes">
                  <Trans id="yes" />
                </option>
                <option value="No">
                  <Trans id="no" />
                </option>
              </Input>
            </div>
          ))}

          <Button
            color="warning"
            onClick={handleSubmit}
            style={styles.submitBtn}
            disabled={assignedRoles.length === 0}
          >
            <Trans id="add_users" />
          </Button>
        </>
      )}
    </div>
  )
}

const styles = {
  container: {
    padding: "20px",
    fontFamily: "Fira Sans",
    backgroundColor: "#fff",
    minHeight: "100vh",
  },
  title: {
    color: "#e36f1e",
    fontWeight: 700,
    fontSize: "22px",
    marginBottom: "24px",
    textAlign: "center",
  },
  card: {
    padding: "16px 20px",
    borderRadius: "8px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    marginBottom: "16px",
    position: "relative",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },
  name: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#333",
  },
  removeIcon: {
    color: "#f44336",
    fontSize: "18px",
    cursor: "pointer",
    transition: "color 0.3s ease",
  },
  groupError: {
    color: "#f44336",
    fontSize: "13px",
    marginBottom: "8px",
    fontStyle: "italic",
  },
  select: {
    width: "100%",
    borderRadius: "6px",
    padding: "8px",
    fontSize: "14px",
    border: "1px solid #e36f1e",
    transition: "border-color 0.3s ease",
  },
  submitBtn: {
    marginTop: "30px",
    width: "100%",
    backgroundColor: "#e36f1e",
    borderColor: "#e36f1e",
    fontWeight: 600,
    padding: "12px",
    fontSize: "15px",
    borderRadius: "6px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    color: "#fff",
  },
  emptyState: {
    textAlign: "center",
    marginTop: "60px",
  },
  emptyText: {
    fontWeight: "bold",
    fontSize: "18px",
    marginBottom: "10px",
    color: "#e36f1e",
  },
  emptySubText: {
    fontSize: "14px",
    color: "#555",
    marginBottom: "20px",
  },
}

const mapStateToProps = (state) => ({
  selectedMembers: Array.isArray(state.searchUsers.selected)
    ? state.searchUsers.selected
    : [],
  group: state.group,
})

const mapDispatchToProps = (dispatch) => ({
  clearSelectedSearchMembers: () => dispatch(clearSelectedSearchMembers()),
  removeSelectedSearchMember: (id) => dispatch(removeSelectedSearchMember(id)),
  setPage: (page) => dispatch(setCurrentPage(page)),
  addMember: (data, isExisting) => dispatch(createMember(data, isExisting)),
})

export default connect(mapStateToProps, mapDispatchToProps)(AssignMemberRoles)
